import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Building2, Award, Instagram, Facebook, Youtube, Music, Linkedin, MessageCircle } from 'lucide-react';
import { buildWhatsAppUrl } from '@/components/WhatsAppLink';
import { cities } from '@/data/cities';
import btoLogo from '@/assets/logo-bto-blanc.webp';

import logo from '@/assets/logo-cygnature.png';
import logoBlanc from '@/assets/logo-cygnature-blanc.png';
import eventAward2023 from '@/assets/event-award-2023.png';
import eventAward2024 from '@/assets/event-award-2024.png';
import eventAward2025 from '@/assets/event-award-2025.png';
import eventAward2026 from '@/assets/event-award-2026.png';

const SiteFooter = () => {
  return (
    <footer className="bg-petrol text-petrol-foreground">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Col 1: Logo + description */}
          <div>
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="mb-5 inline-block">
              <img src={logoBlanc} alt="Cygnature Production" className="h-40 w-auto" width={160} height={160} loading="lazy" />
            </Link>
            <p className="font-heading text-xs italic text-primary/80 mb-2">
              Chaque événement porte votre signature.
            </p>
            <p className="text-[10px] text-petrol-foreground/40 italic mb-4">
              Suivez vos rêves, ils connaissent le chemin.
            </p>
            <p className="text-sm text-petrol-foreground/60 leading-relaxed mb-6">
              L'agence événementielle qui signe des moments d'exception — mariages, galas, spectacles et cérémonies sur-mesure à Troyes, dans l'Aube et partout en France.
            </p>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/40 mb-3">Suivez-nous</p>
            <div className="flex items-center gap-3">
              <a href="https://www.facebook.com/people/CYgnature-Production-Officiel/100083038225185/?locale=fr_FR" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full border border-petrol-foreground/15 flex items-center justify-center text-petrol-foreground/60 hover:text-primary hover:border-primary/40 transition-colors">
                <Facebook size={16} />
              </a>
              <a href="https://www.instagram.com/cygnatureproduction/?hl=fr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full border border-petrol-foreground/15 flex items-center justify-center text-petrol-foreground/60 hover:text-primary hover:border-primary/40 transition-colors">
                <Instagram size={16} />
              </a>
              <a href="https://www.linkedin.com/in/cyrille-lecourt-5425381bb/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-9 h-9 rounded-full border border-petrol-foreground/15 flex items-center justify-center text-petrol-foreground/60 hover:text-primary hover:border-primary/40 transition-colors">
                <Linkedin size={16} />
              </a>
              <a href="https://www.youtube.com/@cygnatureproduction3695" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full border border-petrol-foreground/15 flex items-center justify-center text-petrol-foreground/60 hover:text-primary hover:border-primary/40 transition-colors">
                <Youtube size={16} />
              </a>
            </div>
          </div>

          {/* Col 2: Navigation */}
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/40 mb-5">Navigation</p>
            <nav className="flex flex-col gap-3">
              {[
                { label: 'Accueil', path: '/' },
                { label: 'À propos', path: '/a-propos' },
                { label: 'Services', path: '/services' },
                { label: 'FAQ', path: '/faq' },
                { label: 'Blog', path: '/blog' },
                { label: 'Contact', path: '/contact' },
              ].map((link) => (
                <Link key={link.path} to={link.path} onClick={() => window.scrollTo(0, 0)} className="text-sm text-petrol-foreground/70 hover:text-petrol-foreground transition-colors">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Col 3: Zones d'intervention */}
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/40 mb-5">Zones d'intervention</p>
            <nav className="flex flex-col gap-3">
              {[
                'la-chapelle-saint-luc', 'saint-andre-les-vergers', 'sainte-savine',
                'romilly-sur-seine', 'nogent-sur-seine', 'bar-sur-seine',
                'sens', 'auxerre', 'chalons-en-champagne', 'reims',
              ].map((slug) => {
                const city = cities.find((c) => c.slug === slug);
                if (!city) return null;
                return (
                  <Link key={city.slug} to={`/ville/${city.slug}`} onClick={() => window.scrollTo(0, 0)} className="text-sm text-petrol-foreground/70 hover:text-petrol-foreground transition-colors">
                    {city.prefix.charAt(0).toUpperCase() + city.prefix.slice(1)} {city.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Col 4: Contact */}
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/40 mb-5">Contact</p>
            <div className="flex flex-col gap-3 text-sm text-petrol-foreground/70">
              <a href="https://maps.app.goo.gl/SPqj82CAvgbDAqUU6" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:text-petrol-foreground transition-colors">
                <MapPin size={15} className="text-petrol-foreground/40 shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>8 Rue du Champ de la Gaule<br />10120, Saint-Pouange</span>
              </a>
              <a href="mailto:contact@cygnatureproduction.fr" className="flex items-center gap-3 hover:text-petrol-foreground transition-colors">
                <Mail size={15} className="text-petrol-foreground/40 shrink-0" strokeWidth={1.5} />
                contact@cygnatureproduction.fr
              </a>
              <a href="tel:+33671351550" className="flex items-center gap-3 hover:text-petrol-foreground transition-colors">
                <Phone size={15} className="text-petrol-foreground/40 shrink-0" strokeWidth={1.5} />
                06 71 35 15 50
              </a>
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Contacter sur WhatsApp"
                className="flex items-center gap-3 hover:text-petrol-foreground transition-colors"
              >
                <MessageCircle size={15} className="text-petrol-foreground/40 shrink-0" strokeWidth={1.5} />
                WhatsApp
              </a>
              <div className="text-petrol-foreground/60 text-xs mt-2">
                Lun–Ven : 9h00–19h00<br />Sam : 10h00–18h00
              </div>

              <div className="mt-4 pt-4 border-t border-petrol-foreground/10">
                <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-petrol-foreground/40 mb-2">Licences</p>
                <div className="flex flex-col gap-1.5 text-xs text-petrol-foreground/50">
                  <span>Spectacles 2 : PLATESV-D-2022-008035</span>
                  <span>Spectacles 3 : PLATESV-D-2022-008036</span>
                </div>
                <p className="text-xs text-petrol-foreground/50 mt-3">SIRET : 89847396200014</p>
                <div className="flex items-center gap-3 mt-5 flex-wrap">
                  {[eventAward2023, eventAward2024, eventAward2025, eventAward2026].map((award, i) => (
                    <a key={i} href="https://www.evenementielpourtous.com/cygnature-production-126315" target="_blank" rel="noopener noreferrer" title="Event Awards">
                      <img src={award} alt={`Event Award ${2023 + i}`} className="h-14 w-auto" loading="lazy" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Bottom bar */}
      <div className="border-t border-petrol-foreground/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col items-center gap-3 md:flex-row md:justify-between md:gap-4">
          <p className="text-xs text-petrol-foreground/40">
            © {new Date().getFullYear()} Cygnature Production — Tous droits réservés.
          </p>
          <div className="flex items-center gap-6 text-xs text-petrol-foreground/40">
            <Link to="/mentions-legales" onClick={() => window.scrollTo(0, 0)} className="hover:text-petrol-foreground transition-colors">Mentions légales</Link>
            <Link to="/politique-de-confidentialite" onClick={() => window.scrollTo(0, 0)} className="hover:text-petrol-foreground transition-colors">Confidentialité</Link>
          </div>
          <span className="text-xs text-petrol-foreground/40 flex items-center gap-2">
            Site réalisé par
            <a href="https://b-to.io/" target="_blank" rel="noopener noreferrer">
              <img src={btoLogo} alt="B-to" className="h-6 w-auto" width={72} height={24} />
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};
export default SiteFooter;
